// ============================================================================
// KLINISOL - For Investigators Page
// Partnership information and service expectations
// Pure Tailwind CSS - Card-based layout
// COLOR SCHEME: Slate Grey (#475569) & Magenta (#E91E63)
// ============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { 
    Users, 
    MessageCircle, 
    Award, 
    CheckCircle, 
    Clock, 
    Shield, 
    TrendingUp,
    FileText,
    Heart,
    Activity,
    Stethoscope,
    Target,
    Zap
} from 'lucide-react';

const ForInvestigators = () => {
    return (
        <div className="min-h-screen bg-[#f8fafd]">
            {/* Hero Section */}
            <section className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-16 px-6">
                <div className="max-w-5xl mx-auto text-center">
                    <Users className="w-16 h-16 mx-auto mb-4" />
                    <h1 className="text-4xl md:text-5xl font-bold mb-3">
                        Partner with KLINiSOL
                    </h1>
                    <p className="text-lg opacity-90 mb-2">
                        For Your Investigator-Initiated Trial
                    </p>
                    <p className="text-base opacity-75">
                        30 years of expertise supporting clinical innovation in dermatology, cardiovascular, and gastroenterology research
                    </p>
                </div>
            </section>

            {/* Why Partner Section */}
            <section className="py-16 px-6">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-[#475569] mb-3">
                            Why Partner with KLIN<span className="text-[#E91E63]">i</span>SOL?
                        </h2>
                        <p className="text-lg text-gray-600">
                            A boutique CRO exclusively focused on investigator-initiated trials
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            {
                                icon: Target,
                                title: "IIT Specialists",
                                desc: "Unlike large CROs that focus on sponsor-initiated trials, we exclusively support investigator-initiated research. Your trial is not just another project—it's our specialty."
                            },
                            {
                                icon: Heart,
                                title: "Therapeutic Expertise",
                                desc: "Deep clinical knowledge in dermatology, cardiovascular, and gastroenterology. Our team understands the nuances of your research area and regulatory pathways."
                            },
                            {
                                icon: Shield,
                                title: "30 Years Experience",
                                desc: "Three decades of FDA-regulated clinical trial experience, IRB operations, and regulatory submissions. We've navigated complex studies successfully."
                            },
                            {
                                icon: CheckCircle,
                                title: "Regulatory Readiness",
                                desc: "Systems designed for 21 CFR Part 11 compliance, ICH-GCP E6(R3) aligned operations, and HIPAA-ready infrastructure. Our validated systems and SOPs ensure audit preparedness."
                            },
                            {
                                icon: Users,
                                title: "Personalized Support",
                                desc: "As a boutique CRO, you get dedicated attention from senior staff. No handoffs to junior teams—your success is our priority."
                            },
                            {
                                icon: Zap,
                                title: "Innovative Technology",
                                desc: "Access to our proprietary ILO Platform featuring AI-powered analytics, voice-enabled data capture, and real-time dashboards (pilot deployment 2026)."
                            }
                        ].map((item, index) => (
                            <div 
                                key={index}
                                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg hover:border-[#E91E63] border border-gray-100 transition-all"
                            >
                                <item.icon className="w-12 h-12 text-[#E91E63] mb-4" />
                                <h3 className="text-xl font-bold text-gray-900 mb-3">
                                    {item.title}
                                </h3>
                                <p className="text-gray-600">
                                    {item.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* What to Expect Section */}
            <section className="py-16 px-6 bg-white">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-[#475569] mb-3">
                            What to Expect from Our CRO Services
                        </h2>
                        <p className="text-lg text-gray-600">
                            Transparency and collaboration from concept to completion
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="bg-gradient-to-br from-[#475569]/5 to-[#E91E63]/5 rounded-xl p-8 border border-gray-200 hover:shadow-lg hover:border-[#E91E63] transition-all">
                            <Clock className="w-10 h-10 text-[#E91E63] mb-4" />
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">Timeline Expectations</h3>
                            <ul className="space-y-3 text-gray-700">
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                    <span><strong>Protocol Development:</strong> 4-8 weeks collaborative refinement</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                    <span><strong>Regulatory Submissions:</strong> IND/IDE preparation in 4-6 weeks</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                    <span><strong>Site Activation:</strong> 6-10 weeks from IRB submission to first patient</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                    <span><strong>Study Conduct:</strong> Ongoing monitoring and data management</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                    <span><strong>Final Reporting:</strong> Database lock to final report in 8-12 weeks</span>
                                </li>
                            </ul>
                        </div>

                        <div className="bg-gradient-to-br from-[#475569]/5 to-[#E91E63]/5 rounded-xl p-8 border border-gray-200 hover:shadow-lg hover:border-[#E91E63] transition-all">
                            <Users className="w-10 h-10 text-[#E91E63] mb-4" />
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">Your Dedicated Team</h3>
                            <ul className="space-y-3 text-gray-700">
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                    <span><strong>Project Manager:</strong> Single point of contact for all operational matters</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                    <span><strong>Medical Monitor:</strong> Therapeutic area expert for clinical oversight</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                    <span><strong>Regulatory Affairs:</strong> FDA submission and compliance support</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                    <span><strong>Clinical Monitors:</strong> On-site and remote monitoring specialists</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                    <span><strong>Data Management:</strong> EDC support and database oversight</span>
                                </li>
                            </ul>
                        </div>

                        <div className="bg-gradient-to-br from-[#475569]/5 to-[#E91E63]/5 rounded-xl p-8 border border-gray-200 hover:shadow-lg hover:border-[#E91E63] transition-all">
                            <MessageCircle className="w-10 h-10 text-[#E91E63] mb-4" />
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">Communication Protocols</h3>
                            <ul className="space-y-3 text-gray-700">
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                    <span><strong>Weekly Status Calls:</strong> Regular touchpoints during active phases</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                    <span><strong>Monthly Reports:</strong> Enrollment, monitoring, and safety summaries</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                    <span><strong>24/7 Safety Line:</strong> Immediate response for SAE reporting</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                    <span><strong>Real-Time Dashboard:</strong> Access to enrollment and data metrics</span>
                                </li>
                            </ul>
                        </div>

                        <div className="bg-gradient-to-br from-[#475569]/5 to-[#E91E63]/5 rounded-xl p-8 border border-gray-200 hover:shadow-lg hover:border-[#E91E63] transition-all">
                            <Shield className="w-10 h-10 text-[#E91E63] mb-4" />
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">Quality Assurance</h3>
                            <ul className="space-y-3 text-gray-700">
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                    <span><strong>271+ SOPs:</strong> Comprehensive quality management system</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                    <span><strong>Internal Audits:</strong> Regular quality oversight and CAPA tracking</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                    <span><strong>Audit Readiness:</strong> Inspection-ready documentation standards</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                    <span><strong>Risk-Based Monitoring:</strong> ICH-GCP E6(R3) compliant approaches</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Rights & Responsibilities Section */}
            <section className="py-16 px-6">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-[#475569] mb-3">
                            Your Rights and Responsibilities as Principal Investigator
                        </h2>
                        <p className="text-lg text-gray-600">
                            Understanding your role in investigator-initiated trials
                        </p>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 hover:shadow-lg hover:border-[#E91E63] transition-all">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div>
                                <h3 className="text-xl font-bold text-[#475569] mb-4 flex items-center gap-2">
                                    <Award className="w-6 h-6 text-[#E91E63]" />
                                    Your Rights as PI
                                </h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start gap-2">
                                        <span className="text-[#E91E63] font-bold">•</span>
                                        <span><strong>Scientific Control:</strong> Maintain oversight of study design and conduct</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-[#E91E63] font-bold">•</span>
                                        <span><strong>Publication Rights:</strong> First right to publish study results</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-[#E91E63] font-bold">•</span>
                                        <span><strong>Data Access:</strong> Full access to study data at all times</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-[#E91E63] font-bold">•</span>
                                        <span><strong>Protocol Modifications:</strong> Ability to amend protocol with regulatory approval</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-[#E91E63] font-bold">•</span>
                                        <span><strong>Sponsor Authority:</strong> Final decision-making as regulatory sponsor (if applicable)</span>
                                    </li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="text-xl font-bold text-[#475569] mb-4 flex items-center gap-2">
                                    <CheckCircle className="w-6 h-6 text-[#E91E63]" />
                                    Your Responsibilities
                                </h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start gap-2">
                                        <span className="text-[#E91E63] font-bold">•</span>
                                        <span><strong>Protocol Adherence:</strong> Ensure study conducted per approved protocol</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-[#E91E63] font-bold">•</span>
                                        <span><strong>Informed Consent:</strong> Obtain and document voluntary informed consent</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-[#E91E63] font-bold">•</span>
                                        <span><strong>Safety Reporting:</strong> Report adverse events per regulations and protocol</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-[#E91E63] font-bold">•</span>
                                        <span><strong>Regulatory Compliance:</strong> Maintain FDA and ICH-GCP compliance</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-[#E91E63] font-bold">•</span>
                                        <span><strong>Documentation:</strong> Maintain accurate, complete source documents</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="mt-8 p-6 bg-gradient-to-br from-[#475569]/5 to-[#E91E63]/5 rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-700 leading-relaxed">
                                <strong className="text-[#475569]">KLIN<span className="text-[#E91E63]">i</span>SOL Support:</strong> While you maintain scientific control and regulatory responsibility, KLIN<span className="text-[#E91E63]">i</span>SOL provides comprehensive operational support to ensure compliance with all ICH-GCP and FDA requirements. We handle the complexity so you can focus on the science.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Trial Phases Section */}
            <section className="py-16 px-6 bg-white">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-[#475569] mb-3">
                            How We Support Your IIT from Concept to Completion
                        </h2>
                        <p className="text-lg text-gray-600">
                            Comprehensive support through every phase of your trial
                        </p>
                    </div>

                    <div className="space-y-6">
                        {[
                            {
                                phase: "Phase 1: Planning",
                                icon: FileText,
                                timeline: "Months 1-4",
                                activities: [
                                    "Protocol development and refinement",
                                    "Budget preparation and funding strategy",
                                    "Feasibility assessment and site identification",
                                    "Regulatory strategy and IND/IDE planning",
                                    "Statistical analysis plan development"
                                ]
                            },
                            {
                                phase: "Phase 2: Startup",
                                icon: TrendingUp,
                                timeline: "Months 4-6",
                                activities: [
                                    "IND/IDE submission to FDA",
                                    "IRB submission and approval",
                                    "Site contracts and budgets",
                                    "Clinical trial agreement negotiations",
                                    "Site initiation visits and training"
                                ]
                            },
                            {
                                phase: "Phase 3: Execution",
                                icon: Activity,
                                timeline: "Ongoing",
                                activities: [
                                    "Subject enrollment and data capture",
                                    "On-site and remote monitoring",
                                    "Safety monitoring and AE/SAE reporting",
                                    "Query management and data cleaning",
                                    "Regulatory communications and amendments"
                                ]
                            },
                            {
                                phase: "Phase 4: Closeout",
                                icon: CheckCircle,
                                timeline: "Final 3-4 months",
                                activities: [
                                    "Site closeout visits",
                                    "Database lock and quality review",
                                    "Statistical analysis and reporting",
                                    "Final study report preparation",
                                    "Regulatory submissions and archival"
                                ]
                            }
                        ].map((phase, index) => {
                            const IconComponent = phase.icon;
                            return (
                                <div 
                                    key={index}
                                    className="bg-gradient-to-r from-white to-gray-50 rounded-xl p-8 border-2 border-[#E91E63] shadow-sm hover:shadow-lg transition-all"
                                >
                                    <div className="flex items-start gap-6">
                                        <div className="bg-gradient-to-br from-[#475569] to-[#E91E63] rounded-lg p-4 flex-shrink-0">
                                            <IconComponent className="w-8 h-8 text-white" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between mb-4">
                                                <h3 className="text-2xl font-bold text-gray-900">{phase.phase}</h3>
                                                <span className="text-sm font-semibold text-[#E91E63] bg-[#E91E63]/10 px-3 py-1 rounded-full">
                                                    {phase.timeline}
                                                </span>
                                            </div>
                                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {phase.activities.map((activity, idx) => (
                                                    <li key={idx} className="flex items-start gap-2 text-gray-700">
                                                        <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                                        <span>{activity}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Therapeutic Areas Section */}
            <section className="py-16 px-6">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-[#475569] mb-3">
                            Our Therapeutic Area Expertise
                        </h2>
                        <p className="text-lg text-gray-600">
                            Deep clinical knowledge where it matters most
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            {
                                icon: Activity,
                                title: "Dermatology",
                                areas: ["Psoriasis", "Eczema", "Acne", "Skin Cancer", "Wound Healing", "Rare Skin Diseases"]
                            },
                            {
                                icon: Heart,
                                title: "Cardiovascular",
                                areas: ["Heart Failure (HFrEF/HFpEF)", "Arrhythmias", "Hypertension", "CAD", "Dyslipidemia", "Vascular Disease"]
                            },
                            {
                                icon: Stethoscope,
                                title: "Gastroenterology",
                                areas: ["IBD (Crohn's/UC)", "IBS", "GERD", "NASH", "Cirrhosis", "Celiac Disease"]
                            }
                        ].map((area, index) => {
                            const IconComponent = area.icon;
                            return (
                                <div 
                                    key={index}
                                    className="bg-gradient-to-br from-[#475569]/5 to-[#E91E63]/5 rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-[#E91E63] transition-all"
                                >
                                    <IconComponent className="w-12 h-12 text-[#E91E63] mb-4" />
                                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                        {area.title}
                                    </h3>
                                    <ul className="space-y-2">
                                        {area.areas.map((item, idx) => (
                                            <li key={idx} className="text-sm text-gray-700 flex items-center gap-2">
                                                <span className="w-1.5 h-1.5 bg-[#E91E63] rounded-full"></span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            );
                        })}
                    </div>

                    <div className="text-center mt-8">
                        <Link 
                            to="/therapeutic-areas" 
                            className="inline-flex items-center gap-2 px-6 py-3 bg-white rounded-lg shadow-md hover:shadow-xl border-2 border-[#475569] hover:border-[#E91E63] transition-all group"
                        >
                            <Award className="w-6 h-6 text-[#475569] group-hover:text-[#E91E63] transition-colors" />
                            <span className="font-bold text-gray-900">Learn More About Our Expertise</span>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Final CTA Section */}
            <section className="py-16 px-6 bg-gradient-to-br from-[#475569]/10 to-[#E91E63]/10">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-4xl font-bold text-gray-900 mb-4">
                        Ready to Start Your Investigator-Initiated Trial?
                    </h2>
                    <p className="text-lg text-gray-700 mb-8">
                        Let's discuss how KLIN<span className="text-[#E91E63]">i</span>SOL can support your research goals with personalized, expert CRO services
                    </p>
                    <Link 
                        to="/contact" 
                        className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#475569] to-[#E91E63] rounded-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all group"
                    >
                        <MessageCircle className="w-8 h-8 text-white group-hover:scale-110 transition-transform" />
                        <span className="text-xl font-bold text-white">Let's Connect</span>
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-6 px-6 mt-12">
                <div className="max-w-7xl mx-auto text-center">
                    <h3 className="text-xl font-bold mb-2">KLINiSOL</h3>
                    <p className="text-sm opacity-90 mb-2">
                        &copy; 2025 KLINISOL. All rights reserved.
                    </p>
                    <Link 
                        to="/terms-and-conditions" 
                        className="text-xs opacity-75 hover:opacity-100 hover:underline transition-opacity mb-4 inline-block"
                    >
                        Terms & Conditions
                    </Link>
                    
                    {/* Social Media Links */}
                    <div className="flex items-center justify-center gap-4 mt-4">
                        <a 
                            href="https://www.linkedin.com/company/klinisol/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110"
                            aria-label="LinkedIn"
                        >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                            </svg>
                        </a>
                        
                        <a 
                            href="https://youtube.com/@klinisol721" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110"
                            aria-label="YouTube"
                        >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                            </svg>
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default ForInvestigators;